import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Input,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Title,
} from "chart.js";
import { Result, Search } from "core/types/search.types";
import { SearchTable } from "components/organisms/search-table";
import { validateUrl } from "core/helpers/validate-url";
import { researchApi } from "core/api";
import { LoadingButton } from "@mui/lab";

ChartJS.register(LinearScale, CategoryScale, BarElement, Title);

interface OwnProps {
  selectedResearch: string;
  isResearchLoading: boolean;
  researchDetail: Search;
  isPracticeLoading: boolean;
  generatePractice: (urls: Result[]) => void;
  getSearches: () => void;
}
export type Props = OwnProps;

const SearchDetail: FunctionComponent<Props> = ({
  selectedResearch,
  isResearchLoading,
  researchDetail,
  isPracticeLoading,
  generatePractice,
  getSearches,
}) => {
  const { t } = useTranslation();
  const [manualUrl, setManualUrl] = useState("");
  const [manualUrlTitle, setManualUrlTitle] = useState("");
  const [isManualAddingLoading, setIsManualAddingLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const options: ChartOptions<"bar"> = {
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  const onManualReport = async () => {
    setIsManualAddingLoading(true);
    try {
      const resp = await researchApi.addManual(
        manualUrlTitle,
        manualUrl,
        selectedResearch
      );
      if (resp) {
        setOpenSnackbar(true);
        setMessage(t("searches.success"));
        getSearches();
      }
    } catch (err) {
      setOpenSnackbar(true);
      setMessage(t("searches.error"));
      setIsError(true);
    } finally {
      setIsManualAddingLoading(false);
    }
  };

  return (
    <>
      <Paper
        sx={{
          borderRadius: "10px",
          padding: "20px",
        }}
      >
        {isResearchLoading ? (
          <div
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <CircularProgress color="secondary" size={"100px"} />
          </div>
        ) : (
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              sm={6}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Box>
                <Text>
                  {t("searches.code")} <b>{selectedResearch}</b>
                </Text>
              </Box>

              <Box>
                <Typography variant="body1" display={"flex"}>
                  <b style={{ marginRight: "5px" }}>{t("searches.params")}:</b>{" "}
                  {(researchDetail.params ?? []).map((p, index) => (
                    <Typography key={index}>
                      {p}
                      {researchDetail.params?.length &&
                      researchDetail.params?.length - 1 !== index
                        ? ", "
                        : ""}
                    </Typography>
                  ))}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body1">
                  <b>{t("searches.total")}</b> {researchDetail.results?.length}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {/* <Chart list={sourceList} showLegend /> */}
            </Grid>
          </Grid>
        )}
      </Paper>
      <Paper
        sx={{
          borderRadius: "10px",
          padding: "20px",
          mt: "20px",
        }}
      >
        <Text fontSize={"16px"} fontWeight={"700"} paddingY={"15px"}>
          {t("report.create_custom")}
        </Text>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Input
              value={manualUrlTitle}
              onChange={(ev) => setManualUrlTitle(ev.target.value)}
              fullWidth
              placeholder={t("report.manual_create_title_placeholder")}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              value={manualUrl}
              onChange={(ev) => setManualUrl(ev.target.value)}
              fullWidth
              placeholder={t("report.manual_create_placeholder")}
            />
          </Grid>
          <Grid item xs={2}>
            <LoadingButton
              fullWidth
              variant="contained"
              onClick={onManualReport}
              disabled={!validateUrl(manualUrl) || !manualUrlTitle}
              loading={isManualAddingLoading}
            >
              {t("report.create")}
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
      {researchDetail.results && (
        <Paper
          sx={{
            borderRadius: "10px",
            mt: "20px",
            padding: "20px",
          }}
        >
          <SearchTable
            search={researchDetail}
            detailMode
            style={{
              maxHeight: "460px",
            }}
            generatePractice={generatePractice}
            isPracticeLoading={isPracticeLoading}
          />
        </Paper>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setOpenSnackbar(false);
          setMessage("");
          setIsError(false);
        }}
      >
        <Alert
          onClose={() => {
            setOpenSnackbar(false);
            setMessage("");
            setIsError(false);
          }}
          severity={isError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SearchDetail;
